'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/settings';
import { normalizeLanguage, withLanguageParam } from '@/lib/i18nUrl';

type LanguageSwitcherProps = {
  compact?: boolean;
};

export default function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const router = useRouter();
  const [currentPath, setCurrentPath] = useState('/');

  useEffect(() => {
    const syncFromUrl = () => {
      const { pathname, search, hash } = window.location;
      const params = new URLSearchParams(search);
      const urlLanguage = normalizeLanguage(params.get('lang'));

      setCurrentPath(`${pathname}${search}${hash}` || '/');

      if (i18n.resolvedLanguage !== urlLanguage) {
        i18n.changeLanguage(urlLanguage);
      }
    };

    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);

    return () => window.removeEventListener('popstate', syncFromUrl);
  }, [i18n]);

  function onLanguageChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const nextLanguage = normalizeLanguage(event.target.value);
    const basePath = currentPath || '/';
    const nextPath = withLanguageParam(basePath, nextLanguage);

    i18n.changeLanguage(nextLanguage);
    router.replace(nextPath, { scroll: false });
    setCurrentPath(nextPath);
  }

  return (
    <label className="flex items-center gap-2">
      <span className={compact ? 'sr-only' : 'text-xs font-semibold uppercase tracking-wider text-muted'}>
        {t('language.label')}
      </span>
      <select
        value={normalizeLanguage(i18n.resolvedLanguage) as SupportedLanguage}
        onChange={onLanguageChange}
        className="rounded-xl border border-border bg-white px-3 py-2 text-xs font-semibold text-foreground outline-none focus:border-primary"
        aria-label={t('language.ariaLabel')}
      >
        {SUPPORTED_LANGUAGES.map((languageCode) => (
          <option key={languageCode} value={languageCode}>
            {LANGUAGE_LABELS[languageCode]}
          </option>
        ))}
      </select>
    </label>
  );
}
